import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { makeStyles } from '@material-ui/core/styles';

import LandingSection from '../../MyComponents/LandingSections/LandingSection';
import SectionEyebrow from '../../MyComponents/LandingSections/SectionEyebrow';
import HighlightMark from '../../MyComponents/LandingSections/HighlightMark';
import store from '../../redux/store';
import { showError, showSuccess } from '../../redux/actions';
import { CARD, KICKER, PILL_PRIMARY, cx } from '../Home/HomeDashboard/dashboardStyles';
import { COLORS2, REDESIGN_COLORS, REDESIGN_FONTS } from '../../constants';

const ONE_COL = '@media (max-width: 860px)';

const useStyles = makeStyles(() => ({
    intro: {
        maxWidth: '820px',
        margin: '0 auto',
        textAlign: 'center',
    },
    eyebrowRow: {
        display: 'flex',
        justifyContent: 'center',
        marginBottom: '22px',
    },
    title: {
        margin: '0 0 18px',
        fontSize: 'clamp(2rem, 4.4vw, 3.4rem)',
        lineHeight: 1.1,
        letterSpacing: '-0.035em',
        fontWeight: REDESIGN_FONTS.weightHeading,
        color: REDESIGN_COLORS.dark,
        textWrap: 'balance',
    },
    lead: {
        maxWidth: '680px',
        margin: '0 auto',
        color: REDESIGN_COLORS.body,
        fontSize: '1.06rem',
        lineHeight: 1.6,
    },
    layout: {
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1.2fr) minmax(0, 0.8fr)',
        gap: '22px',
        alignItems: 'start',
        [ONE_COL]: {
            gridTemplateColumns: 'minmax(0, 1fr)',
        },
    },
    form: {
        ...CARD,
        display: 'grid',
        gap: '16px',
        padding: 'clamp(22px, 2.8vw, 34px)',
    },
    twoUp: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '16px',
        [ONE_COL]: {
            gridTemplateColumns: 'minmax(0, 1fr)',
        },
    },
    field: {
        display: 'grid',
        gap: '6px',
    },
    label: {
        color: REDESIGN_COLORS.dark,
        fontSize: '0.86rem',
        fontWeight: REDESIGN_FONTS.weightStrong,
    },
    input: {
        boxSizing: 'border-box',
        width: '100%',
        minHeight: '50px',
        padding: '12px 16px',
        border: `1px solid ${REDESIGN_COLORS.line}`,
        borderRadius: '14px',
        background: REDESIGN_COLORS.cream,
        fontFamily: REDESIGN_FONTS.body,
        fontSize: '1rem',
        fontWeight: 600,
        color: REDESIGN_COLORS.dark,
        outline: 'none',
        transition: 'border-color 150ms ease, box-shadow 150ms ease',
        '&::placeholder': {
            color: REDESIGN_COLORS.muted,
            fontWeight: 500,
            opacity: 1,
        },
        '&:focus': {
            borderColor: REDESIGN_COLORS.primary,
            boxShadow: '0 0 0 3px rgba(16, 169, 179, 0.15)',
        },
    },
    textarea: {
        minHeight: '150px',
        resize: 'vertical',
        lineHeight: 1.5,
    },
    invalid: {
        borderColor: COLORS2.virtualRed,
        '&:focus': {
            borderColor: COLORS2.virtualRed,
            boxShadow: '0 0 0 3px rgba(243, 83, 37, 0.15)',
        },
    },
    // The star rating: five buttons, filled up to the chosen one.
    stars: {
        display: 'flex',
        gap: '4px',
    },
    star: {
        display: 'grid',
        placeItems: 'center',
        width: '36px',
        height: '36px',
        padding: 0,
        border: 0,
        borderRadius: '50%',
        background: 'none',
        color: REDESIGN_COLORS.line,
        cursor: 'pointer',
        transition: 'color 120ms ease, transform 120ms ease',
        '&:hover': {
            transform: 'scale(1.12)',
        },
    },
    starOn: {
        color: REDESIGN_COLORS.highlight,
    },
    actions: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '14px',
        marginTop: '4px',
    },
    submit: {
        ...PILL_PRIMARY,
        minHeight: '52px',
        padding: '0 26px',
    },
    hint: {
        margin: 0,
        color: REDESIGN_COLORS.muted,
        fontSize: '0.86rem',
        fontWeight: 600,
    },
    kicker: {
        ...KICKER,
        marginBottom: '6px',
    },
    address: {
        margin: '0 0 16px',
        color: REDESIGN_COLORS.body,
        fontSize: '0.92rem',
        lineHeight: 1.55,
        '&:last-child': {
            marginBottom: 0,
        },
    },
}));

/**
 * The dev centre and legal addresses both message pages show in their side
 * card.
 */
export const Addresses = () => {
    const classes = useStyles();

    return (
        <>
            <span className={classes.kicker}>
                <FormattedMessage id="contactUs.devCenter" />
            </span>
            <p className={classes.address}>
                Str. Primăverii
                <br />
                Dumbrăvița, Timiș, România
            </p>
            <span className={classes.kicker}>
                <FormattedMessage id="contactUs.legalInformation" />
            </span>
            <p className={classes.address}>
                VIRTUAL BOARD SRL
                <br />
                Dumbrăvița, Timiș, România
            </p>
        </>
    );
};

/**
 * A "write to us" page on the landing's patterns: eyebrow, headline, lead, a
 * form card (optional star rating, name, phone, email, required message)
 * beside a side card. `send(payload)` posts the message and resolves on
 * success, after which the page shows the sent toast and returns home.
 * Copy comes from `${keyPrefix}.title|lead|form.placeholder|form.hint`.
 */
const MessagePage = ({ keyPrefix, eyebrowId, rating = false, send, side }) => {
    const classes = useStyles();
    const history = useHistory();
    const intl = useIntl();
    const [ratingValue, setRatingValue] = useState(0);
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [message, setMessage] = useState('');
    const [error, setError] = useState(false);
    const [busy, setBusy] = useState(false);

    const t = (id) => intl.formatMessage({ id });

    const submit = (event) => {
        event.preventDefault();
        if (busy) return;
        if (!message.trim()) {
            store.dispatch(showError('NO_FEEDBACK_PRESENT'));
            setError(true);
            return;
        }
        setBusy(true);
        send({ ratingValue, name, email, phone, message })
            .then(() => {
                store.dispatch(showSuccess('FEEDBACK_SENT'));
                history.push('/');
            })
            .catch(() => setBusy(false));
    };

    return (
        <>
            <LandingSection id="top" maxWidth={1080} paddingTop={46} paddingBottom={24}>
                <div className={classes.intro}>
                    <div className={classes.eyebrowRow}>
                        <SectionEyebrow>
                            <FormattedMessage id={eyebrowId} />
                        </SectionEyebrow>
                    </div>
                    <h1 className={classes.title}>
                        <FormattedMessage
                            id={`${keyPrefix}.title`}
                            values={{ mark: (chunks) => <HighlightMark>{chunks}</HighlightMark> }}
                        />
                    </h1>
                    <p className={classes.lead}>
                        <FormattedMessage id={`${keyPrefix}.lead`} />
                    </p>
                </div>
            </LandingSection>

            <LandingSection maxWidth={1080} paddingTop={16} paddingBottom={64}>
                <div className={classes.layout}>
                    <form className={classes.form} onSubmit={submit} noValidate>
                        {rating ? (
                            <div className={classes.field}>
                                <span className={classes.label}>{t('redesign.contact.rating.label')}</span>
                                <div className={classes.stars} role="radiogroup">
                                    {[1, 2, 3, 4, 5].map((value) => (
                                        <button
                                            key={value}
                                            type="button"
                                            role="radio"
                                            aria-checked={ratingValue === value}
                                            aria-label={intl.formatMessage(
                                                { id: 'redesign.contact.rating.star' },
                                                { count: value }
                                            )}
                                            onClick={() => setRatingValue(ratingValue === value ? 0 : value)}
                                            className={cx(classes.star, value <= ratingValue && classes.starOn)}
                                        >
                                            <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                                                <path d="M12 2.5l2.9 6.1 6.6.8-4.9 4.6 1.3 6.6L12 17.3l-5.9 3.3 1.3-6.6L2.5 9.4l6.6-.8L12 2.5z" />
                                            </svg>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        ) : null}
                        <div className={classes.twoUp}>
                            <label className={classes.field}>
                                <span className={classes.label}>{t('contactUs.name')}</span>
                                <input
                                    type="text"
                                    name="name"
                                    autoComplete="name"
                                    value={name}
                                    onChange={(event) => setName(event.target.value)}
                                    className={classes.input}
                                />
                            </label>
                            <label className={classes.field}>
                                <span className={classes.label}>{t('contactUs.phone')}</span>
                                <input
                                    type="tel"
                                    name="phone"
                                    autoComplete="tel"
                                    value={phone}
                                    onChange={(event) => setPhone(event.target.value)}
                                    className={classes.input}
                                />
                            </label>
                        </div>
                        <label className={classes.field}>
                            <span className={classes.label}>{t('contactUs.email')}</span>
                            <input
                                type="email"
                                name="email"
                                autoComplete="email"
                                value={email}
                                onChange={(event) => setEmail(event.target.value)}
                                className={classes.input}
                            />
                        </label>
                        <label className={classes.field}>
                            <span className={classes.label}>{t('contactUs.message')} *</span>
                            <textarea
                                name="message"
                                required
                                aria-invalid={error}
                                value={message}
                                placeholder={t(`${keyPrefix}.form.placeholder`)}
                                onChange={(event) => {
                                    setMessage(event.target.value);
                                    if (error && event.target.value.trim()) setError(false);
                                }}
                                className={cx(classes.input, classes.textarea, error && classes.invalid)}
                            />
                        </label>
                        <div className={classes.actions}>
                            <button type="submit" disabled={busy} className={classes.submit}>
                                <FormattedMessage id="contactUs.sendMessageButton" />
                                <span aria-hidden="true">→</span>
                            </button>
                            <p className={classes.hint}>
                                <FormattedMessage id={`${keyPrefix}.form.hint`} />
                            </p>
                        </div>
                    </form>

                    {side}
                </div>
            </LandingSection>
        </>
    );
};

export default MessagePage;
